import React, { useMemo, useCallback, useLayoutEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { ButtonGroup, SettingsItem } from 'design-system';
import { AppView } from 'design-system/AppView';
import { CardView } from 'design-system/CardView';
import { IconBoldCalendar, IconLightBulb } from 'design-system/icons';
import Button from 'design-system/Button';
import IconStatusUp from 'design-system/icons/IconStatusUp';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import SellSecclAssetOptionsModal from '../components/SellSecclAssetOptionsModal';
import { selectHasAssetSwitch } from '../reducers/selectors';
import { isWeb } from '../../../constants';
export const strings = {
    title: 'Before you sell your investment',
    description: 'Selling an investment can affect long-term returns. We want to make sure you have the right information before you continue',
    cards: [
        {
            title: 'Investing works best over time',
            description: 'Most investments are designed to be held for at least 5 years',
        },
        {
            title: 'Short-term drops are normal',
            description: 'Markets move up and down. Selling during a dip can lock in losses',
        },
        {
            title: 'Selling is irreversible once placed',
            description: 'Once a sell order is placed, it can’t be cancelled and the final amount may change',
        },
    ],
    buttons: {
        stillWantToSell: 'I still want to sell',
        dontSell: 'Don’t sell',
    },
};
const BeforeYouSellScreen = ({ route, navigation }) => {
    const colors = useColors();
    const assetId = 'assetId' in route.params ? route.params.assetId : undefined;
    const sellType = 'sellType' in route.params ? route.params.sellType : undefined;
    const hasAssetSwitch = useAppSelector(selectHasAssetSwitch);
    const onSell = useCallback(() => {
        if (assetId) {
            if (hasAssetSwitch) {
                SellSecclAssetOptionsModal.show(assetId);
            }
            else {
                navigation.navigate('InvestSell', { assetId });
            }
        }
        else if (sellType) {
            navigation.navigate('SelectAssetToSell', { sellType });
        }
    }, [assetId, hasAssetSwitch, sellType]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const data = useMemo(() => [
        {
            title: strings.cards[0].title,
            description: strings.cards[0].description,
            icon: <IconBoldCalendar gradientKey="lightPurple"/>,
        },
        {
            title: strings.cards[1].title,
            description: strings.cards[1].description,
            icon: <IconStatusUp gradientKey="orange"/>,
        },
        {
            title: strings.cards[2].title,
            description: strings.cards[2].description,
            icon: <IconLightBulb gradientKey="skyBlue" height={30} width={30}/>,
        },
    ], []);
    return (<AppView type="secondary" avoidHeader>
      <ScreenInfoHeader title={strings.title} subTitle={strings.description}/>
      <View>
        {data.map(({ title, description, icon }) => (<CardView key={title} noMarginTop style={styles.card}>
            <SettingsItem disabled icon={icon} title={title} description={description}/>
          </CardView>))}
      </View>
      <ButtonGroup type="floating">
        <Button brandReversed title={strings.buttons.stillWantToSell} onPress={onSell}/>
        <Button brand title={strings.buttons.dontSell} onPress={navigation.goBack}/>
      </ButtonGroup>
    </AppView>);
};
const styles = StyleSheet.create({
    // The row has an 8px margin on web; this puts its icon 16px from the card edge
    card: isWeb ? { paddingHorizontal: 8 } : {},
});
export default BeforeYouSellScreen;
